import type {
	AnimationsBehavior,
	BackgroundMedia,
	BackgroundMediaFillLayerCSSVars,
	MediaCSSVars,
	VideoFillLayerCSSVars,
} from '@wix/thunderbolt-becky-types'
import { getFillLayersColorsVars } from '@wix/thunderbolt-becky-root'
import type { CompInfo } from '@wix/thunderbolt-components-native'
import _ from 'lodash'
import { getMediaTransformsCSSVars, shouldRenderBackgroundMediaLayer } from '../BackgroundMedia/BackgroundMedia.utils'
import { getBackgroundOverlayFillLayerCSSVars } from '../BackgroundOverlay/BackgroundOverlay.utils'
import { getImageFillLayerCSSVars } from '../Image/Image.utils'
import { hasBackgroundScrubEffect, isMediaFullScreenByEffect } from '../Media/Media.utils'
import { getMediaPaddingLayerCSSVars } from '../MediaPadding/MediaPadding.utils'
import { getVideoFillLayerCSSVars } from '../Video/Video.utils'

export const getBackgroundMediaFillLayerCSSVars = ({
	animations,
	viewMode,
}: Pick<CompInfo, 'experiments' | 'viewMode'> & {
	animations: Array<AnimationsBehavior>
}): BackgroundMediaFillLayerCSSVars | undefined => {
	const shouldRenderBackgroundMedia = shouldRenderBackgroundMediaLayer({
		animations,
		viewMode,
	})
	if (shouldRenderBackgroundMedia) {
		const hasBgScrubEffect = hasBackgroundScrubEffect({ animations, viewMode })
		const isFullScreenByEffect = isMediaFullScreenByEffect({
			animations,
			viewMode,
		})

		const backgroundMediaCSSVars = {
			'--fill-layer-background-media-position': isFullScreenByEffect ? 'fixed' : 'absolute',
			'--fill-layer-background-media-pointer-events': hasBgScrubEffect ? 'none' : 'auto',
		} as const

		return backgroundMediaCSSVars
	}
}

export const getFillLayersMediaVars = ({
	animations,
	compProps,
	compDesign,
	compId,
	backgroundMedia,
	isNonAdaptiveMobile,
	experiments,
	isMobileView,
	viewMode,
}: Pick<CompInfo, 'compId' | 'compProps' | 'compDesign' | 'experiments' | 'isMobileView' | 'viewMode'> & {
	animations: Array<AnimationsBehavior>
	isNonAdaptiveMobile: boolean
	backgroundMedia: BackgroundMedia
}): Partial<MediaCSSVars & VideoFillLayerCSSVars> => {
	const imageLayerCSSVars = getImageFillLayerCSSVars(backgroundMedia, compId)
	const videoLayerCSSVars = getVideoFillLayerCSSVars(backgroundMedia)
	const backgroundMediaCSSVars = getBackgroundMediaFillLayerCSSVars({
		animations,
		experiments,
		viewMode,
	})

	const backgroundOverlayLayerCSSVars = getBackgroundOverlayFillLayerCSSVars({
		animations,
		experiments,
		isMobileView,
		isNonAdaptiveMobile,
		viewMode,
		backgroundMedia,
	})

	const mediaPaddingLayerCSSVars = getMediaPaddingLayerCSSVars({ compProps, isMobileView })
	const transformsData = compDesign ? getMediaTransformsCSSVars({ compDesign, backgroundMedia }) : {}

	return _.pickBy(
		{
			...imageLayerCSSVars,
			...videoLayerCSSVars,
			...backgroundMediaCSSVars,
			...backgroundOverlayLayerCSSVars,
			...mediaPaddingLayerCSSVars,
			...transformsData,
		},
		(v) => v || typeof v === 'number'
	)
}

export const getFillLayersCSSVars = ({
	animations,
	compProps,
	compDesign,
	siteColors,
	compId,
	backgroundMedia,
	isNonAdaptiveMobile,
	experiments,
	isMobileView,
	viewMode,
}: Pick<
	CompInfo,
	'compId' | 'compProps' | 'compDesign' | 'siteColors' | 'experiments' | 'isMobileView' | 'viewMode'
> & {
	animations: Array<AnimationsBehavior>
	isNonAdaptiveMobile: boolean
	backgroundMedia: BackgroundMedia
}): Partial<MediaCSSVars & VideoFillLayerCSSVars> => ({
	...getFillLayersMediaVars({
		animations,
		compProps,
		compDesign,
		compId,
		backgroundMedia,
		isNonAdaptiveMobile,
		experiments,
		isMobileView,
		viewMode,
	}),
	...getFillLayersColorsVars({ siteColors, backgroundMedia }),
})
